import { Menu as MenuIcon } from "@mui/icons-material";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import {
  Avatar,
  Box,
  CssBaseline,
  CSSObject,
  Drawer as MuiDrawer,
  GlobalStyles,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  styled,
  Theme,
  Toolbar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AppBar from "@mui/material/AppBar";
import { ListItems } from "AppNav";
import useCurrentUser from "hooks/useCurrentUser";
import { TestWarehouseSelect } from "pages/Layouts/TestWarehouseSelect";
import { ReactComponent as LogoIcon } from "images/logo.svg";
import { useEffect, useState } from "react";
import { Outlet, useLocation, useRouteError } from "react-router-dom";
import stringToColor from "utils/stringToColor";
import "assets/App.css";
import { PageTitleContainer } from "pages/Layouts/components/PageTitleContainer";
import AppErrorPage from "pages/AppErrorPage";

const drawerWidth: number = 300;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",

  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
        "& .MuiListItemIcon-root, & .MuiListItemAvatar-root": {
          marginRight: "32px",
        },
        "& .MuiListItemText-root": {
          opacity: 1,
        },
        "& .MuiAvatar-root": {
          width: 36,
          height: 36,
        },
        [theme.breakpoints.down("lg")]: {
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
        },
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
        "& .MuiListItemIcon-root": {
          marginRight: "auto",
        },
        "& .MuiListItemText-root": {
          opacity: 0,
        },
        "& .MuiListItemText-root + .MuiSvgIcon-root": {
          flex: "1 1 auto",
          opacity: 0,
        },
        "& .MuiAvatar-root": {
          width: 24,
          height: 24,
        },
        [theme.breakpoints.down("lg")]: {
          width: 0,
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          "& .MuiDrawer-paper": {
            width: 0,
          },
        },
      },
    },
  ],
}));

export function LoggedInLayout() {
  const theme = useTheme();
  const isTabletView = useMediaQuery(theme.breakpoints.down("lg"));
  const [open, setOpen] = useState(() => {
    return localStorage.getItem("appNav.open") === "true";
  });
  const location = useLocation();
  const { user, currentUserPermissions } = useCurrentUser();
  const error = useRouteError();

  function handleToggle() {
    setOpen((prev) => {
      const next = !prev;
      if (!isTabletView) {
        localStorage.setItem("appNav.open", next ? "true" : "false");
      }
      return next;
    });
  }

  useEffect(() => {
    if (isTabletView) {
      setOpen(false);
    } else {
      if (localStorage.getItem("appNav.open") === "true") setOpen(true);
    }
  }, [isTabletView, location]);

  return (
    <Box
      sx={{
        display: "grid",
        height: "100%",
        gridTemplateColumns: "auto 1fr",
        gridTemplateRows: "auto 1fr",
        gridTemplateAreas: "'topbar topbar' 'sidebar main'",
        backgroundColor: theme.palette.mode === "light" ? "#F9FBFD" : theme.palette.grey[900],
      }}
    >
      <GlobalStyles styles={{ html: { height: "100%" }, body: { height: "100%" }, "#root": { height: "100%" } }} />
      <CssBaseline />

      <Box
        sx={{
          gridArea: "topbar",
          [theme.breakpoints.up("lg")]: {
            display: "none",
          },
        }}
      >
        <AppBar variant="outlined" position="static" elevation={0} sx={{ backgroundColor: "background.default" }}>
          <Toolbar sx={{ px: 1 }}>
            <IconButton
              color="primary"
              onClick={handleToggle}
              aria-label={open ? "Collapse Sidebar" : "Expand Sidebar"}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
      </Box>

      <Drawer variant={"permanent"} open={open} as="nav" sx={{ gridArea: "sidebar" }}>
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: [1],
          }}
        >
          <LogoIcon />
          <IconButton color="primary" aria-label={open ? "Collapse Sidebar" : "Expand Sidebar"} onClick={handleToggle}>
            {open ? <MenuOpenIcon /> : <MenuIcon />}
          </IconButton>
        </Toolbar>
        <ListItems open={open} setOpen={setOpen} currentUserPermissions={currentUserPermissions} />
        <Box sx={{ flexGrow: 1, display: "flex", alignItems: "flex-end" }}>
          {user && (
            <ListItem
              dense={!user.canSelectWarehouse && !!user.warehouseName}
              sx={{
                backgroundColor: "background.paper",
                boxShadow: `0 -8px 8px ${theme.palette.background.paper}`,
              }}
            >
              <ListItemAvatar sx={{ minWidth: 0 }}>
                <Avatar sx={{ width: 24, height: 24, bgcolor: stringToColor(user.email) }}>
                  {user.email.charAt(0).toUpperCase()}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={user.email}
                secondary={
                  user.canSelectWarehouse ? (
                    <TestWarehouseSelect warehouseId={user.warehouseId} warehouses={user.warehouses} />
                  ) : (
                    user.warehouseName
                  )
                }
                secondaryTypographyProps={{ component: "div" }}
              />
            </ListItem>
          )}
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          backgroundColor: theme.palette.mode === "light" ? "#F9FBFD" : theme.palette.grey[900],
          padding: 3,
          paddingBlock: 2,
          display: "flex",
          flexDirection: "column",
          gridArea: "main",
          minWidth: 0,
          minHeight: 0,
          transition: theme.transitions.create(["margin-left", "padding"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          [theme.breakpoints.down("lg")]: {
            padding: theme.spacing(3),
          },
          [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(2),
          },
        }}
      >
        <PageTitleContainer />
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {error ? <AppErrorPage /> : <Outlet />}
        </Box>
      </Box>
    </Box>
  );
}
